/**
 * StepDataViewer Component
 *
 * Tabbed viewer showing ABox Elements, Changes, and URLs for a given step.
 * Each tab fetches data lazily on first selection.
 */

import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { DataTable, Column } from '../ui/DataTable';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { playgroundService } from '../../services/playgroundApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface StepDataViewerProps {
  sessionId: string;
  stepNumber: number;
  onClose: () => void;
  /** Whether the session is still running (browser alive for highlight/HTML). */
  isLive?: boolean;
  /** Called when user selects an ABox element (for cross-component highlighting). */
  onElementSelect?: (elementId: number | null) => void;
  /** Externally controlled highlighted element ID. */
  highlightedId?: number | null;
  /** Session-level learnings from past sessions (shown in Learnings tab). */
  learnings?: string[];
  /** Force-select a specific tab on mount/update (e.g. 'learnings'). */
  initialTab?: TabKey;
}

type TabKey = 'abox' | 'abox_short' | 'changes' | 'urls' | 'tool_data' | 'llm_data' | 'learnings';

interface TabState<T> {
  data: T[] | null;
  isLoading: boolean;
  error: string | null;
}

// --- ABox types & columns ---

interface ABoxElement {
  element_id: number;
  type: string;
  html_tag: string;
  name: string;
  value: string;
  visible: boolean;
  required: boolean;
  readonly: boolean;
  checked: boolean;
  blocked: boolean;
  input_type: string;
  label: string;
  placeholder: string;
  parent_text: string;
  class_names: string;
  group: string;
  layer: string;
  expanded: boolean;
  selected: boolean;
  has_triggers: boolean;
  // New fields from Agent.extractABox
  position: { x: number; y: number; w: number; h: number } | null;
  style: { color: string; font_size: number } | null;
  listeners: string[] | null;
  trigger_hints: string[] | null;
  form_action: string;
  form_method: string;
  interaction: string;
  hint: string;
  associated_button: number | null;
  validation: Record<string, any> | null;
}

const aboxColumns: Column<ABoxElement>[] = [
  { key: 'element_id', label: 'ID', sortable: true, width: '60px' },
  { key: 'type', label: 'Type', sortable: true, width: '80px' },
  { key: 'html_tag', label: 'Tag', sortable: true, width: '70px' },
  { key: 'name', label: 'Name', sortable: true },
  { key: 'value', label: 'Value', sortable: true },
  { key: 'visible', label: 'Visible', sortable: true, width: '65px' },
  { key: 'required', label: 'Required', sortable: true },
  { key: 'readonly', label: 'Readonly', sortable: true },
  { key: 'checked', label: 'Checked', sortable: true },
  { key: 'blocked', label: 'Blocked', sortable: true },
  { key: 'input_type', label: 'Input Type', sortable: true },
  { key: 'label', label: 'Label', sortable: true },
  { key: 'placeholder', label: 'Placeholder', sortable: true },
  { key: 'parent_text', label: 'Parent Text', sortable: true },
  { key: 'class_names', label: 'Class Names', sortable: true },
  { key: 'group', label: 'Group', sortable: true },
  { key: 'layer', label: 'Layer', sortable: true },
  { key: 'expanded', label: 'Expanded', sortable: true },
  { key: 'selected', label: 'Selected', sortable: true },
  { key: 'has_triggers', label: 'Has Triggers', sortable: true },
  { key: 'interaction', label: 'Interaction', sortable: true },
  { key: 'hint', label: 'Hint', sortable: true },
  { key: 'trigger_hints', label: 'Trigger Hints', sortable: false, render: (v: any) => v && Array.isArray(v) ? <span className="text-xs">{v.join(', ')}</span> : <span className="text-gray-300">-</span> },
  { key: 'listeners', label: 'Listeners', sortable: false, render: (v: any) => v && Array.isArray(v) ? <span className="text-xs">{v.join(', ')}</span> : <span className="text-gray-300">-</span> },
  { key: 'form_action', label: 'Form Action', sortable: true },
  { key: 'form_method', label: 'Form Method', sortable: true, width: '80px' },
  { key: 'associated_button', label: 'Assoc. Button', sortable: true, width: '90px' },
  { key: 'position', label: 'Position', sortable: false, render: (v: any) => v ? <span className="text-xs font-mono">{v.x},{v.y} {v.w}x{v.h}</span> : <span className="text-gray-300">-</span> },
];

// --- ABox Short: 2-column view (ID + compact format line) ---

interface ABoxShortRow {
  element_id: number;
  compact: string;
}

/** Parse the compact text from EVA into short rows: one row per element line. */
function parseCompactToShortRows(compact: string): ABoxShortRow[] {
  if (!compact) return [];
  const rows: ABoxShortRow[] = [];
  const idRe = /\b(\d+)\|/;
  for (const line of compact.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    // Info entries are JSON — skip them for the short table
    if (trimmed.startsWith('{')) continue;
    const m = idRe.exec(trimmed);
    const id = m ? parseInt(m[1], 10) : 0;
    rows.push({ element_id: id, compact: trimmed });
  }
  return rows;
}

/** Fallback: build compact-ish lines from full ABoxElement (for old sessions without abox_compact). */
function toABoxShortRows(elements: ABoxElement[]): ABoxShortRow[] {
  return elements.map((el) => {
    const tag = el.html_tag || 'div';
    const type = el.type || '?';
    const name = el.name || '';
    const identity = type === tag
      ? (name ? `${el.element_id}|${name}` : `${el.element_id}`)
      : (name ? `${el.element_id}|${type}|${name}` : `${el.element_id}|${type}`);
    const parts: string[] = [];
    if (!el.visible) parts.push('hidden');
    if (el.blocked) parts.push('blocked');
    if (el.required) parts.push('required');
    if (el.readonly) parts.push('readonly');
    if (el.checked) parts.push('checked');
    if (el.has_triggers) parts.push('has_triggers');
    if (el.interaction) parts.push(el.interaction);
    if (el.value) parts.push(`val="${el.value.length > 25 ? el.value.slice(0, 25) + '…' : el.value}"`);
    if (el.placeholder) parts.push(`ph="${el.placeholder.length > 20 ? el.placeholder.slice(0, 20) + '…' : el.placeholder}"`);
    if (el.trigger_hints?.length) parts.push(`triggers=${el.trigger_hints.join(',')}`);
    if (el.parent_text) {
      const pt = el.parent_text.length > 30 ? el.parent_text.slice(0, 30) + '…' : el.parent_text;
      parts.push(`in:"${pt}"`);
    }
    const attrStr = parts.length ? ' ' + parts.join(' ') : '';
    return { element_id: el.element_id, compact: `<${tag} ${identity}${attrStr}/>` };
  });
}

function renderCompactCol(value: any): ReactNode {
  if (!value) return <span className="text-gray-300">-</span>;
  return <span className="font-mono text-xs text-gray-700 whitespace-pre-wrap">{String(value)}</span>;
}

const aboxShortColumns: Column<ABoxShortRow>[] = [
  { key: 'element_id', label: 'ID', sortable: true, width: '50px' },
  { key: 'compact', label: 'Compact Format', sortable: false, render: renderCompactCol },
];

// --- Changes types & columns ---

interface Change {
  change_type: string;
  element_id: number;
  element_name: string;
  element_type: string;
  property_diffs: Record<string, { from: any; to: any }> | null;
}

function renderChangeType(value: any): ReactNode {
  const colorMap: Record<string, string> = {
    added: 'bg-green-100 text-green-700',
    removed: 'bg-red-100 text-red-700',
    modified: 'bg-yellow-100 text-yellow-700',
  };
  const cls = colorMap[value] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${cls}`}>
      {value}
    </span>
  );
}

function renderPropertyDiffs(value: any): ReactNode {
  if (!value || typeof value !== 'object') {
    return <span className="text-gray-400">-</span>;
  }
  const entries = Object.entries(value);
  if (entries.length === 0) {
    return <span className="text-gray-400">-</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([prop, diff]: [string, any]) => (
        <span
          key={prop}
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-100 rounded text-xs"
        >
          <span className="font-medium text-gray-700">{prop}:</span>
          <span className="text-red-500">{String(diff?.from ?? '-')}</span>
          <span className="text-gray-400">{'\u2192'}</span>
          <span className="text-green-600">{String(diff?.to ?? '-')}</span>
        </span>
      ))}
    </div>
  );
}

const changesColumns: Column<Change>[] = [
  {
    key: 'change_type',
    label: 'Type',
    sortable: true,
    width: '90px',
    render: renderChangeType,
  },
  { key: 'element_id', label: 'Element ID', sortable: true, width: '90px' },
  { key: 'element_name', label: 'Name', sortable: true },
  { key: 'element_type', label: 'Element Type', sortable: true, width: '100px' },
  {
    key: 'property_diffs',
    label: 'Property Changes',
    render: renderPropertyDiffs,
  },
];

// --- URLs types & columns ---

interface UrlEntry {
  name: string;
  url: string;
  is_new: boolean;
}

function renderUrl(value: any): ReactNode {
  if (!value) return <span className="text-gray-400">-</span>;
  const str = String(value);
  const truncated = str.length > 80 ? str.slice(0, 80) + '...' : str;
  return (
    <a
      href={str}
      target="_blank"
      rel="noopener noreferrer"
      title={str}
      className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
    >
      {truncated}
    </a>
  );
}

function renderIsNew(value: any): ReactNode {
  if (value === true) {
    return (
      <span className="inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
        NEW
      </span>
    );
  }
  return <span className="text-gray-400">-</span>;
}

const urlsColumns: Column<UrlEntry>[] = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'url', label: 'URL', sortable: true, render: renderUrl },
  { key: 'is_new', label: 'New', sortable: true, width: '60px', render: renderIsNew },
];

// --- Tabs config ---

interface TabConfig {
  key: TabKey;
  label: string;
  endpoint: string;
  responseKey: string;
}

const dataTabs: TabConfig[] = [
  { key: 'abox', label: 'ABox Elements', endpoint: 'abox', responseKey: 'elements' },
  { key: 'changes', label: 'Changes', endpoint: 'changes', responseKey: 'changes' },
  { key: 'urls', label: 'URLs', endpoint: 'urls', responseKey: 'urls' },
];

const allTabKeys: { key: TabKey; label: string }[] = [
  { key: 'abox_short', label: 'ABox Short' },
  { key: 'abox', label: 'ABox Full' },
  { key: 'changes', label: 'Changes' },
  { key: 'urls', label: 'URLs' },
  { key: 'tool_data', label: 'Tool Data' },
  { key: 'llm_data', label: 'LLM Data' },
  { key: 'learnings', label: 'Learnings' },
];

export const StepDataViewer: React.FC<StepDataViewerProps> = ({
  sessionId,
  stepNumber,
  onClose,
  isLive = false,
  onElementSelect,
  highlightedId,
  learnings,
  initialTab,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab || 'abox_short');
  const [tabStates, setTabStates] = useState<Record<TabKey, TabState<any>>>({
    abox: { data: null, isLoading: false, error: null },
    abox_short: { data: null, isLoading: false, error: null },
    changes: { data: null, isLoading: false, error: null },
    urls: { data: null, isLoading: false, error: null },
    tool_data: { data: null, isLoading: false, error: null },
    llm_data: { data: null, isLoading: false, error: null },
    learnings: { data: null, isLoading: false, error: null },
  });
  // Tool data is not an array — it's { input, output, planning_text? }
  const [toolData, setToolData] = useState<{ input: string; output: string; planning_text?: string } | null>(null);
  const [toolDataLoading, setToolDataLoading] = useState(false);
  const [toolDataError, setToolDataError] = useState<string | null>(null);
  // LLM data — { prompt, token_usage }
  const [llmData, setLlmData] = useState<{ prompt: string; token_usage: string } | null>(null);
  const [llmDataLoading, setLlmDataLoading] = useState(false);
  const [llmDataError, setLlmDataError] = useState<string | null>(null);
  const [actionLabel, setActionLabel] = useState<string | null>(null);
  const [internalHighlightId, setInternalHighlightId] = useState<number | null>(null);
  const highlightedElementId = highlightedId !== undefined ? highlightedId : internalHighlightId;

  const fetchTab = useCallback(
    async (tabKey: TabKey) => {
      const config = dataTabs.find((t) => t.key === tabKey);
      if (!config) return;

      setTabStates((prev) => ({
        ...prev,
        [tabKey]: { ...prev[tabKey], isLoading: true, error: null },
      }));

      try {
        const url = `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/steps/${stepNumber}/${config.endpoint}`;
        const response = await authenticatedFetch(url);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const json = await response.json();
        const items = json[config.responseKey] || [];

        // When fetching abox, also populate abox_short from the compact text
        if (tabKey === 'abox' && json.abox_compact) {
          const shortRows = parseCompactToShortRows(json.abox_compact);
          setTabStates((prev) => ({
            ...prev,
            [tabKey]: { data: items, isLoading: false, error: null },
            abox_short: { data: shortRows, isLoading: false, error: null },
          }));
        } else {
          setTabStates((prev) => ({
            ...prev,
            [tabKey]: { data: items, isLoading: false, error: null },
          }));
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch data';
        setTabStates((prev) => ({
          ...prev,
          [tabKey]: { data: null, isLoading: false, error: msg },
        }));
      }
    },
    [sessionId, stepNumber]
  );

  // Handle ABox row click for element highlighting
  const handleABoxRowClick = useCallback(
    async (row: ABoxElement) => {
      const elementId = row.element_id;

      if (highlightedElementId === elementId) {
        // Toggle off
        setInternalHighlightId(null);
        if (onElementSelect) onElementSelect(null);
        return;
      }

      setInternalHighlightId(elementId);
      if (onElementSelect) onElementSelect(elementId);
    },
    [highlightedElementId, onElementSelect]
  );

  // Switch to requested tab when initialTab changes
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  // Reset tab states and fetch step action when step number changes
  useEffect(() => {
    setTabStates({
      abox: { data: null, isLoading: false, error: null },
      abox_short: { data: null, isLoading: false, error: null },
      changes: { data: null, isLoading: false, error: null },
      urls: { data: null, isLoading: false, error: null },
      tool_data: { data: null, isLoading: false, error: null },
      llm_data: { data: null, isLoading: false, error: null },
      learnings: { data: null, isLoading: false, error: null },
    });
    setToolData(null);
    setToolDataError(null);
    setLlmData(null);
    setLlmDataError(null);
    setInternalHighlightId(null);
    setActionLabel(null);

    // Fetch step metadata to build action label (e.g. "click 42")
    (async () => {
      try {
        const url = `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/steps`;
        const response = await authenticatedFetch(url);
        if (!response.ok) return;
        const json = await response.json();
        const steps: any[] = json.steps || [];
        const step = steps.find((s: any) => s.step_number === stepNumber);
        if (!step) return;

        const toolName = (step.tool_name || '').replace(/_/g, ' ').replace(/ tool$/, '').replace(/ element$/, '');
        let args: any = {};
        try { args = typeof step.tool_args === 'string' ? JSON.parse(step.tool_args) : (step.tool_args || {}); } catch {}

        const parts = [toolName];
        if (args.element_id != null) parts.push(String(args.element_id));
        if (args.text) parts.push(`"${args.text.length > 30 ? args.text.slice(0, 30) + '...' : args.text}"`);
        if (args.url) parts.push(args.url.length > 40 ? args.url.slice(0, 40) + '...' : args.url);
        setActionLabel(parts.join(' '));
      } catch {}
    })();
  }, [sessionId, stepNumber]);

  // Fetch tool data on first select
  const fetchToolData = useCallback(async () => {
    setToolDataLoading(true);
    setToolDataError(null);
    try {
      const url = `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/steps/${stepNumber}/tool-data`;
      const response = await authenticatedFetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json = await response.json();
      setToolData({ input: json.input || '', output: json.output || '', planning_text: json.planning_text || '' });
    } catch (err) {
      setToolDataError(err instanceof Error ? err.message : 'Failed to fetch tool data');
    } finally {
      setToolDataLoading(false);
    }
  }, [sessionId, stepNumber]);

  // Fetch LLM data on first select
  const fetchLlmData = useCallback(async () => {
    setLlmDataLoading(true);
    setLlmDataError(null);
    try {
      const url = `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/steps/${stepNumber}/llm-data`;
      const response = await authenticatedFetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json = await response.json();
      setLlmData({ prompt: json.prompt || '', token_usage: json.token_usage || '' });
    } catch (err) {
      setLlmDataError(err instanceof Error ? err.message : 'Failed to fetch LLM data');
    } finally {
      setLlmDataLoading(false);
    }
  }, [sessionId, stepNumber]);

  // Fetch active tab data on first select
  useEffect(() => {
    if (activeTab === 'learnings') {
      // Learnings are passed as props, no fetch needed
      return;
    }
    if (activeTab === 'tool_data') {
      if (!toolData && !toolDataLoading && !toolDataError) {
        fetchToolData();
      }
      return;
    }
    if (activeTab === 'llm_data') {
      if (!llmData && !llmDataLoading && !llmDataError) {
        fetchLlmData();
      }
      return;
    }
    // abox_short shares data with abox — fetch abox if needed
    const fetchKey: TabKey = activeTab === 'abox_short' ? 'abox' : activeTab;
    const state = tabStates[fetchKey];
    if (state.data === null && !state.isLoading && !state.error) {
      fetchTab(fetchKey);
    }
    // Derive abox_short from full abox data if compact text wasn't available (old sessions)
    if (activeTab === 'abox_short' && tabStates.abox.data && !tabStates.abox_short.data) {
      setTabStates((prev) => ({
        ...prev,
        abox_short: { data: toABoxShortRows(prev.abox.data || []), isLoading: false, error: null },
      }));
    }
  }, [activeTab, tabStates, fetchTab, toolData, toolDataLoading, toolDataError, fetchToolData, llmData, llmDataLoading, llmDataError, fetchLlmData]);

  const currentState = activeTab === 'learnings'
    ? { data: learnings || [], isLoading: false, error: null }
    : activeTab === 'tool_data'
    ? { data: toolData ? [toolData] : null, isLoading: toolDataLoading, error: toolDataError }
    : activeTab === 'llm_data'
    ? { data: llmData ? [llmData] : null, isLoading: llmDataLoading, error: llmDataError }
    : tabStates[activeTab === 'abox_short' ? 'abox' : activeTab];

  const getTabLabel = (tab: { key: TabKey; label: string }): string => {
    if (tab.key === 'learnings') {
      return learnings && learnings.length > 0 ? `${tab.label} (${learnings.length})` : tab.label;
    }
    // abox_short uses abox count
    const countKey: TabKey = tab.key === 'abox_short' ? 'abox' : tab.key;
    const state = tabStates[countKey];
    const count = state.data ? state.data.length : null;
    return count !== null ? `${tab.label} (${count})` : tab.label;
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-800">
          Step {stepNumber} Data
          {actionLabel && (
            <span className="ml-2 px-2 py-0.5 rounded text-xs font-mono font-medium bg-blue-50 text-blue-700">
              {actionLabel}
            </span>
          )}
        </h3>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
          title="Close"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 flex-shrink-0">
        {allTabKeys.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {getTabLabel(tab)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {currentState.isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        ) : currentState.error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-red-500 text-sm">
              <p>Failed to load data</p>
              <p className="text-xs text-gray-400 mt-1">{currentState.error}</p>
              <button
                onClick={() => activeTab === 'tool_data' ? fetchToolData() : activeTab === 'llm_data' ? fetchLlmData() : fetchTab(activeTab)}
                className="mt-2 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'abox_short' && (
              <DataTable<ABoxShortRow>
                columns={aboxShortColumns}
                data={tabStates.abox_short.data || []}
                emptyMessage="No ABox elements for this step"
                onRowClick={(row) => {
                  const full = (tabStates.abox.data || []).find((e: ABoxElement) => e.element_id === row.element_id);
                  if (full) handleABoxRowClick(full);
                }}
                highlightRowIndex={
                  highlightedElementId !== null
                    ? (tabStates.abox_short.data || []).findIndex(
                        (r: ABoxShortRow) => r.element_id === highlightedElementId
                      )
                    : undefined
                }
              />
            )}
            {activeTab === 'abox' && (
              <DataTable<ABoxElement>
                columns={aboxColumns}
                data={currentState.data || []}
                emptyMessage="No ABox elements for this step"
                onRowClick={handleABoxRowClick}
                highlightRowIndex={
                  highlightedElementId !== null
                    ? (currentState.data || []).findIndex(
                        (r: ABoxElement) => r.element_id === highlightedElementId
                      )
                    : undefined
                }
              />
            )}
            {activeTab === 'changes' && (
              <DataTable<Change>
                columns={changesColumns}
                data={currentState.data || []}
                emptyMessage="No changes for this step"
              />
            )}
            {activeTab === 'urls' && (
              <DataTable<UrlEntry>
                columns={urlsColumns}
                data={currentState.data || []}
                emptyMessage="No URLs for this step"
              />
            )}
            {activeTab === 'tool_data' && toolData && (
              <div className="h-full overflow-auto p-4 space-y-4">
                {toolData.planning_text && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Planning</h4>
                    <pre className="text-xs text-gray-800 bg-blue-50 rounded p-3 whitespace-pre-wrap break-all border border-blue-200">
                      {toolData.planning_text}
                    </pre>
                  </div>
                )}
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Input</h4>
                  <pre className="text-xs text-gray-800 bg-gray-50 rounded p-3 whitespace-pre-wrap break-all max-h-[45%] overflow-auto border border-gray-200">
                    {toolData.input || <span className="text-gray-400 italic">No input data</span>}
                  </pre>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Output</h4>
                  <pre className="text-xs text-gray-800 bg-gray-50 rounded p-3 whitespace-pre-wrap break-all max-h-[45%] overflow-auto border border-gray-200">
                    {toolData.output || <span className="text-gray-400 italic">No output data</span>}
                  </pre>
                </div>
              </div>
            )}
            {activeTab === 'tool_data' && !toolData && !toolDataLoading && (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No tool data for this step
              </div>
            )}
            {activeTab === 'llm_data' && llmData && (
              <div className="h-full overflow-auto p-4 space-y-4">
                {llmData.token_usage && (() => {
                  try {
                    const tu = JSON.parse(llmData.token_usage);
                    const fmt = (n: number) => n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n);
                    return (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Token Usage</h4>
                        <div className="flex gap-3 text-xs font-mono bg-gray-50 rounded p-3 border border-gray-200">
                          <span>Input: <strong>{fmt(tu.input)}</strong></span>
                          <span>Output: <strong>{fmt(tu.output)}</strong></span>
                          <span>Thinking: <strong>{fmt(tu.thinking)}</strong></span>
                          <span>Cached: <strong>{fmt(tu.cached)}</strong></span>
                        </div>
                      </div>
                    );
                  } catch { return null; }
                })()}
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">LLM Prompt (Last User Content)</h4>
                  <pre className="text-xs text-gray-800 bg-blue-50 rounded p-3 whitespace-pre-wrap break-all overflow-auto border border-blue-200">
                    {llmData.prompt || <span className="text-gray-400 italic">No data — only available in step mode</span>}
                  </pre>
                </div>
              </div>
            )}
            {activeTab === 'llm_data' && !llmData && !llmDataLoading && (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No LLM data — only available in step mode
              </div>
            )}
            {activeTab === 'learnings' && (
              <div className="h-full overflow-auto p-4">
                {learnings && learnings.length > 0 ? (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Learnings from Past Sessions ({learnings.length})
                    </h4>
                    <div className="space-y-2">
                      {learnings.map((learning, i) => (
                        <div
                          key={i}
                          className="text-sm text-gray-800 bg-purple-50 rounded p-3 border border-purple-200"
                        >
                          {learning}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                    No learnings from past sessions
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
